'use client';

import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

export function QueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
            // Retry once on transient errors, but NEVER retry on 429
            // (rate-limited) — that just amplifies the storm. The page
            // surfaces the error and the user/RQ refetches once the window
            // clears; doubling up the request just wastes the next budget.
            // Also skip 4xx that aren't transient (404/403 don't get better
            // by retrying).
            retry: (failureCount, error) => {
              const status = (error as { status?: number }).status;
              if (status === 429) return false;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 1;
            },
            // Belt-and-braces against React error #185 notify storms: if a
            // queryKey is accidentally reference-unstable, refetchOnMount can
            // re-trigger the query on every re-mount the error boundary does,
            // turning a single render bug into an infinite loop. Disable it
            // here so the cached value is reused until staleTime elapses.
            refetchOnMount: false,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
